import React, { useMemo } from 'react';
import themes, { styled } from '~/themes';
import { Box, Container, Divider, Skeleton, Typography } from '@mui/material';
import NoImage from '~/assets/images/icons/no-avatar.svg';
import NotFoundImg from '~/assets/images/background/setup-your-store-background.png';
import { useGetTrackQuery } from '~/services/api/track';
import { useParams } from 'react-router-dom';
import { TrackStatus, TaskStatus } from '~/models/track';
import { Stack } from '@mui/system';
import { IPlanTier } from '~/models/plan';
import moment from 'moment-timezone';
import TrackPhone from '~/assets/images/icons/track-phone.svg';
import { formatSimpleTime, getContrastColor } from '~/utils/common';
import TrackProgress from './TrackProgress';
import TrackRate from './TrackRate';
import { formatDateTime, TrackVerticalProgress } from './TrackVerticalProgress';

export const StyledTextDesc = styled(Typography)(({ theme }) => ({
  color: theme.color.violet500,
  fontSize: 14,
  fontWeight: 500,
  marginBottom: '8px !important',
}));

export const StyledDriverName = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  textAlign: 'center',
  color: theme.color.gray900,
  fontSize: 14,
  background: theme.bg.gray300,
  padding: theme.spacing(0.6, 1.2),
  borderRadius: theme.spacing(0.5),
}));

export const StyledDriverAvatar = styled('img')(({ theme }) => ({
  width: theme.spacing(5.2),
  height: theme.spacing(5.2),
  borderRadius: theme.spacing(2.6),
}));

export const StyledDriverInfo = styled(Box)(({ theme }) => ({
  'textAlign': 'right',
  'display': 'flex',
  'flexDirection': 'column',
  'gap': '4px',
  '& > div': {
    '&:first-child': {
      color: theme.color.violet500,
      fontWeight: 'bold',
      fontSize: 18,
    },
    '&:last-child': {
      color: 'rgba(51, 54, 61, 0.40)',
      fontSize: 14,
    },
  },
}));

export const StyledTrackPhoneButton = styled('a')(({ theme }) => ({
  position: 'absolute',
  top: 1,
  left: theme.spacing(3.5),
}));

export const StyledEstTimeTitle = styled(Typography)(({ theme }) => ({
  color: theme.color.gray900,
  fontSize: 16,
  marginTop: theme.spacing(2),
}));

interface ITrack {
  preview?: boolean;
  exampleData?: any;
}

export const Track: React.FC<ITrack> = ({ preview = false, exampleData }) => {
  const { code } = useParams();

  const { data: rawData, isLoading: isFetching } = useGetTrackQuery({
    params: { code },
    enabled: !preview,
  });

  const data = useMemo(() => {
    if (preview) return exampleData;
    return rawData;
  }, [preview, rawData]);

  const textColor = getContrastColor(data?.brand_color || '#F2F2F2');

  const isRating =
    data?.progress_bar === 1 &&
    data.status === TaskStatus.Completed &&
    data.org_tier === IPlanTier.Advance;

  const isDelivered = data?.status === TaskStatus.Completed;

  const isCancelled = data?.status === TaskStatus.Cancelled;

  const isReturned = data?.status === TaskStatus.Returned;

  const isReturning = data?.status === TaskStatus.Returning;

  const orderMessage = useMemo(() => {
    const now = data?.timezone ? moment().tz(data.timezone) : moment().local();

    if (isDelivered) return 'Your order has been successfully delivered.';
    if (isReturning)
      return 'This order is being returned to the store. The delivery was unsuccessful or refused by the recipient.';
    if (data?.progress_bar < 0.75) return data?.message || '';
    if (data?.progress_bar === 0.75)
      return data?.estimated_delivery_time > 0
        ? `Our driver is on their way to you. Estimated arrival time is around ${formatSimpleTime(
            now.clone().add(data?.estimated_delivery_time, 'm'),
          )}`
        : 'Our driver is on their way to you.';
    if (data?.progress_bar === 0.9)
      return `Your order is almost at your doorstep. Estimated arrival time is around ${formatSimpleTime(
        now.clone().add(data?.estimated_delivery_time, 'm'),
      )}`;
    return '';
  }, [data?.message, data?.timezone, isDelivered, isReturning]);

  const orderNumber = useMemo(() => {
    if (!data?.order_number) return '';
    return data?.order_number?.includes('-')
      ? data?.order_number
      : `#${data?.order_number}`;
  }, [data?.order_number]);

  const statusTitle = useMemo(() => {
    if (isCancelled) return 'Delivery is cancelled';
    if (isReturned) return 'Order is returned';
    if (isDelivered) return 'Order is delivered';
    if (isReturning) return 'Order is returning';

    switch (data?.progress_bar) {
      case 0.25:
        return 'Order is being processed';
      case 0.5:
        return 'In Transit';
      case 0.75:
        return 'Out for Delivery';
      case 0.9:
        return 'Arriving soon';
      default:
        return '';
    }
  }, [data?.progress_bar, isReturned, isCancelled]);

  const renderTitle = () => {
    let rightContent = formatDateTime(data?.created_at, {}, data?.timezone);
    if (isCancelled) {
      rightContent = 'Cancelled';
    }
    if (isReturned) {
      rightContent = 'Returned';
    }
    if (isDelivered) {
      rightContent = 'Delivered';
    }

    return (
      <Stack
        alignItems='center'
        direction='row'
        justifyContent='space-between'
        sx={{
          background: data?.brand_color || '#F2F2F2',
          py: 1.4,
          px: 1.6,
        }}
      >
        <Typography fontSize={18} fontWeight='700' color={textColor}>
          Order {orderNumber}
        </Typography>
        <Typography fontSize={14} color={textColor}>
          {rightContent}
        </Typography>
      </Stack>
    );
  };

  const renderContent = () => {
    if (!data?.order_number && !isFetching) {
      return (
        <Stack
          sx={{
            padding: themes.spacing(3, 1.5),
            justifyContent: 'center',
            alignItems: 'center',
            mt: '25%',
          }}
          spacing={2}
        >
          <img src={NotFoundImg} alt='404.png' width={350} height={230} />
          <Typography variant='h1' fontSize={40}>
            404
          </Typography>
          <Typography variant='body1' color={themes.color.gray900}>
            Something went wrong
          </Typography>
        </Stack>
      );
    }

    if (isFetching && !preview) {
      return (
        <Stack spacing={1.5} py={3} px={1.5}>
          <Skeleton height={40} width='50%' />
          <Skeleton width='90%' />
          <Skeleton width='30%' />
          <Stack spacing={1}>
            <Skeleton width='20%' />
            <Skeleton width='22%' />
            <Skeleton width='30%' />
            <Skeleton width='15%' />
          </Stack>
          <Divider />
          <Skeleton width='25%' />
          <Skeleton width='20%' />
          <Skeleton width='20%' />
          <Skeleton width='20%' />
          <Skeleton width='20%' />
        </Stack>
      );
    }

    if (isRating) {
      return <TrackRate data={data} />;
    }

    return (
      <Stack p={1.6} color={themes.color.violet500}>
        <Typography
          variant='h1'
          fontSize={18}
          color={themes.color.violet500}
          mb={0.8}
        >
          {statusTitle}
        </Typography>
        {!(isCancelled || isReturned) && (
          <Box mb={1.6} mt={0.8}>
            <TrackProgress progress={(data?.progress_bar || 0) * 100} />
          </Box>
        )}
        <Typography fontSize={14}>{orderMessage}</Typography>
        {Boolean(
          data?.driver_name &&
            data?.message &&
            !isDelivered &&
            !(isCancelled || isReturned) &&
            data?.message !== TrackStatus.OrderIsConfirmed,
        ) && (
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: themes.spacing(1.6),
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <StyledDriverAvatar
                src={
                  data?.driver_avatar && data?.driver_avatar_type
                    ? `data:${data.driver_avatar_type};base64,${data.driver_avatar}`
                    : NoImage
                }
                alt='avatar'
              />
              {data?.phone && (
                <StyledTrackPhoneButton href={`tel:${data?.phone}`}>
                  <img src={TrackPhone} alt='phone' />
                </StyledTrackPhoneButton>
              )}
            </Box>
            <StyledDriverInfo>
              <div>{data?.plate_number || ''}</div>
              <div>{data?.vehicle_body || ''}</div>
            </StyledDriverInfo>
          </Stack>
        )}
        {Boolean(data?.driver_name) &&
          !isDelivered &&
          !(isCancelled || isReturned) && (
            <StyledDriverName>{`${data?.driver_name} is taking care of your package`}</StyledDriverName>
          )}
        <TrackVerticalProgress
          value={
            data.status === TaskStatus.Cancelled ||
            data.status === TaskStatus.Returned
              ? 1
              : data.progress_bar
          }
          data={data}
        />
        <Divider sx={{ my: 1.6 }} />
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
          }}
        >
          {data?.pickup && (
            <Stack
              spacing={1}
              fontSize={14}
              color={themes.color.violet500}
              flex={1}
            >
              <StyledTextDesc>Pickup Address</StyledTextDesc>
              <span>{data?.pickup?.name || ''}</span>
              <span>{data?.pickup?.address_1 || ''}</span>
              {data?.pickup?.address_2 && (
                <span>{data?.pickup?.address_2}</span>
              )}
              <span>{`${data?.pickup?.city}, ${data?.pickup?.state} ${data?.pickup?.postcode}`}</span>
            </Stack>
          )}

          <Stack
            spacing={1}
            fontSize={14}
            color={themes.color.violet500}
            flex={1}
          >
            <StyledTextDesc>Delivery Address</StyledTextDesc>
            <span>{data?.delivery_name || ''}</span>
            <span>{data?.address_1 || ''}</span>
            {data?.address_2 && <span>{data?.address_2}</span>}
            <span>{data?.address_body || ''}</span>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <Container
      sx={{
        p: '0px !important',
        maxWidth: '600px !important',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        [themes.breakpoints.down('lg')]: {
          maxWidth: '100% !important',
        },
      }}
    >
      {renderTitle()}
      {renderContent()}
    </Container>
  );
};
